import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getSubscriptionReport } from '../../api/subscriptionReport';
import { getMemberEngagement } from '../../api/memberEngagement';
import MemberEngagementDrawer from '../member-engagement-container/MemberEngagementDrawer';
import SAMPLE_DB from './sampleData';
import styles from './styles';
import { STATUS_DESCRIPTION, statusLabel } from '../../utils/memberStatus';

/**
 * Subscription Report tab.
 *
 * Native render (no iframe) scoped under `.favor-sr`, mirroring the Channel
 * Attribution / Messaging Activity tabs. Ported from the standalone mockup — the
 * aggregate blocks (KPIs, at-a-glance + revenue strips, revenue-by-month chart,
 * weekly members, subscriber movement, plan economics) come from one report
 * payload; the Subscribers table filters/pages server-side via query params.
 *
 * Live data: GET /admin/analytics/stat/getSubscriptionReport. On any failure the
 * tab falls back to the mockup sample so it never renders blank.
 */
// Plan rows are DYNAMIC. The backend decides which plans exist and their display
// order (`DB.planOrder`), because pricing is per tier x gender and lives in
// `subscription_plans` — so a new variant (e.g. a Luxury women's plan) gets its own
// row with no change here. Keys today: lux / gold / goldF.
const DEFAULT_ORDER = ['lux', 'luxF', 'gold', 'goldF']; // premium first; only for the sample fallback
const orderOf = (DB) => ((DB && Array.isArray(DB.planOrder) && DB.planOrder.length)
  ? DB.planOrder
  : DEFAULT_ORDER.filter((k) => DB && DB.cube && DB.cube[k]));
// Chart stack: reverse of display order, so the premium plan sits on top.
const stackOf = (DB) => [...orderOf(DB)].reverse();
const PCOL = {
  lux: 'var(--p-lux)', luxF: 'var(--p-lux-f)', gold: 'var(--p-gold)', goldF: 'var(--p-gold-f)',
};
const PTXT = {
  lux: '#ffffff', luxF: '#ffffff', gold: '#ffffff', goldF: '#ffffff',
};
const PAGE_SIZE = 25;
// Subscriber-table filters in their "nothing selected" state. Named so the Clear
// button and the initial state can't drift apart.
const EMPTY_SF = { q: '', plan: 'all', status: 'all', gender: 'all', from: '', to: '' };
const isFiltered = (sf) => Object.keys(EMPTY_SF).some((k) => sf[k] !== EMPTY_SF[k]);

// Today as the date inputs write it (YYYY-MM-DD), in IST.
//
// It MUST be the IST day, not the UTC one and not the viewer's local one: the
// backend reads `from`/`to` as inclusive IST calendar days, so those are the days
// the picker is really choosing between. The obvious `new Date().toISOString()
// .slice(0, 10)` is the UTC day, which until 05:30 IST is still YESTERDAY — it
// would lock the admin out of selecting the current day every morning.
// `en-CA` formats as YYYY-MM-DD, which is exactly the input's value format, so the
// string comparisons below ("v > today") are safe lexicographic ones.
const IST_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
});
const istToday = () => IST_DAY_FMT.format(new Date());
// A future date can only ever select nothing, so don't let it be set at all.
// `max` alone stops the picker; this also catches a date TYPED into the field,
// which the native control happily accepts past its own max.
const notFuture = (v, today) => (v && v > today ? today : v);

// The report is denominated in INR (the backend normalises every currency to
// rupees). Grouping is en-IN so figures read 3,00,000 rather than 300,000.
const money = (x) => `₹${Math.round(x).toLocaleString('en-IN')}`;
// Compact INR for tight chart labels — lakh / crore are the Indian conventions.
const moneyK = (x) => {
  const v = Math.round(x);
  const a = Math.abs(v);
  if (a >= 1e7) return `₹${(v / 1e7).toFixed(2).replace(/\.00$/, '')}Cr`;
  if (a >= 1e5) return `₹${(v / 1e5).toFixed(1).replace(/\.0$/, '')}L`;
  if (a >= 1e3) return `₹${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return `₹${v}`;
};
// Headline figure: show full rupees until it reaches a crore.
const moneyM = (x) => (Math.abs(x) >= 1e7 ? moneyK(x) : money(x));
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const S = (DB, t) => DB.cube[t].m;

// Money for a cube cell. A cube row is [new, churned, active, newRev, churnRev,
// activeRev] — counts in 0-2, the matching INR in 3-5, summed server-side from
// each member's OWN rate.
//
// Revenue is READ, never recomputed as `count × DB.prices[t]`. That old form
// assumed every member of a plan pays the same price, which stops being true the
// moment the catalogue is re-priced (Gold ₹10,000 -> ₹7,000, Luxury ₹12,000 ->
// ₹10,000 in Jul 2026) or a gateway carries its own price (the PayPal $90 Gold
// plan): it under-stated MRR by ₹5,000 and the month's new revenue by ₹11,200,
// and it re-valued all 13 months of history at today's price on every change.
// `DB.prices[t]` is now only the "₹X/mo" label beside a plan's name.
//
// The mockup sample fallback still carries 3-element rows, so fall back to the
// old derivation when the revenue slot is absent.
//
// Slot names mirror the controller's C_* constants: 0-2 are counts (new, churned,
// active), 3-5 the matching revenue. REV_SLOT maps a count slot to its revenue
// twin, so `R(DB, t, i, C_ACT)` reads "active revenue".
const C_NEW = 0;
const C_CHN = 1;
const C_ACT = 2;
const REV_SLOT = { [C_NEW]: 3, [C_CHN]: 4, [C_ACT]: 5 };
const R = (DB, t, i, cnt) => {
  const row = S(DB, t)[i];
  const v = row[REV_SLOT[cnt]];
  return v != null ? v : row[cnt] * DB.prices[t];
};

// ---- chart HTML builders (ported verbatim; injected via innerHTML) ----------
const buildMainChart = (DB, isM) => {
  // The price shown is the plan's CURRENT list price. Revenue is not derived from
  // it (see R()), so for a plan whose members sit on more than one price the bar
  // will not equal members × this figure — the tooltip says so rather than leaving
  // it looking like an arithmetic error.
  const legend = orderOf(DB).map((t) => `<span><i class="sw" style="background:${PCOL[t]}"></i>${DB.planNames[t]} <span style="color:var(--txt-3)" title="${esc(`Current list price. Members who joined before a price change keep the price they signed up at, so revenue is the sum of what each member actually pays — not members × ${money(DB.prices[t])}.`)}">${money(DB.prices[t])}/mo</span></span>`).join('');
  const totals = [];
  let hmax = 1;
  for (let i = 0; i < 13; i += 1) {
    let tot = 0;
    orderOf(DB).forEach((t) => { tot += isM ? R(DB, t, i, C_ACT) : S(DB, t)[i][C_ACT]; });
    totals.push(tot);
    hmax = Math.max(hmax, tot);
  }
  const H = 168;
  let html = '';
  for (let i = 0; i < 13; i += 1) {
    let segs = '';
    const stack = stackOf(DB);
    stack.forEach((t, si) => {
      const v = isM ? R(DB, t, i, C_ACT) : S(DB, t)[i][C_ACT];
      let h = Math.round((v / hmax) * H);
      if (v > 0 && h < 2) h = 2;
      const lab = h >= 15 ? `<span class="sn2" style="color:${PTXT[t]}">${isM ? moneyK(v) : v.toLocaleString()}</span>` : '';
      // Last segment in stack order is the visual top of the bar.
      segs += `<div class="seg${si === stack.length - 1 ? ' top' : ''}" style="height:${h}px;background:${PCOL[t]}">${lab}</div>`;
    });
    const tip = `${DB.months[i].l} ${DB.months[i].y}: ${orderOf(DB).map((t) => {
      const v = isM ? R(DB, t, i, C_ACT) : S(DB, t)[i][C_ACT];
      return `${DB.planNames[t]} ${isM ? money(v) : v.toLocaleString()}`;
    }).join(' · ')}`;
    const lbl = DB.months[i].l + ((i === 0 || i === 12 || DB.months[i].l === 'Jan') ? `<small>${DB.months[i].y}</small>` : '');
    html += `<div class="mcol${i === 12 ? ' cur' : ''}"><div class="mbars" title="${esc(tip)}"><div class="vbar"><span class="cnt">${isM ? moneyK(totals[i]) : totals[i].toLocaleString()}</span>${segs}</div></div><div class="mlab">${lbl}</div></div>`;
  }
  return { legend, chart: html };
};

// Cash collected in each month — `DB.collected`, built server-side from the
// Completed charges DATED in that month, whatever the member's status is today.
//
// Same bar geometry as buildMainChart so the two read as one family, but it answers
// a different question and the numbers are MEANT to differ: the chart above is a run
// rate (what today's subscribers bill per month, so a cancelled member vanishes from
// every month after they left), this one is money in the bank (a member who paid in
// March keeps March even after cancelling). A quarterly/annual charge lands whole in
// the month it was taken rather than being spread, so a renewal burst can tower over
// MRR and a quiet month can read ₹0 while MRR is unchanged.
const buildCashChart = (DB) => {
  const C = DB.collected;
  const keys = orderOf(DB).filter((t) => C.byPlan && C.byPlan[t]);
  const n = C.total.length;
  const totals = C.total.map((v) => v || 0);
  const hmax = Math.max(1, ...totals);
  const H = 168;
  const at = (t, i) => ((C.byPlan[t] && C.byPlan[t][i]) || 0);
  let html = '';
  for (let i = 0; i < n; i += 1) {
    let segs = '';
    const stack = [...keys].reverse();
    stack.forEach((t, si) => {
      const v = at(t, i);
      let h = Math.round((v / hmax) * H);
      if (v > 0 && h < 2) h = 2;
      const lab = h >= 15 ? `<span class="sn2" style="color:${PTXT[t]}">${moneyK(v)}</span>` : '';
      segs += `<div class="seg${si === stack.length - 1 ? ' top' : ''}" style="height:${h}px;background:${PCOL[t]}">${lab}</div>`;
    });
    const parts = keys.filter((t) => at(t, i) > 0).map((t) => `${DB.planNames[t]} ${money(at(t, i))}`);
    const nc = (C.charges && C.charges[i]) || 0;
    const tip = `${DB.months[i].l} ${DB.months[i].y}: ${money(totals[i])} received`
      + `${parts.length ? ` · ${parts.join(' · ')}` : ''}`
      + ` · ${nc} ${nc === 1 ? 'charge' : 'charges'}`;
    const lbl = DB.months[i].l + ((i === 0 || i === n - 1 || DB.months[i].l === 'Jan') ? `<small>${DB.months[i].y}</small>` : '');
    html += `<div class="mcol${i === n - 1 ? ' cur' : ''}"><div class="mbars" title="${esc(tip)}"><div class="vbar"><span class="cnt">${moneyK(totals[i])}</span>${segs}</div></div><div class="mlab">${lbl}</div></div>`;
  }
  // Legend carries each plan's share of the whole window, since that is the figure
  // the eye can't add up off a stacked chart.
  const legend = keys.map((t) => {
    const sum = C.byPlan[t].reduce((a, b) => a + (b || 0), 0);
    return `<span><i class="sw" style="background:${PCOL[t]}"></i>${DB.planNames[t]} <b>${money(sum)}</b></span>`;
  }).join('');
  const charges = (C.charges || []).reduce((a, b) => a + (b || 0), 0);
  return { legend, chart: html, total: C.windowTotal != null ? C.windowTotal : totals.reduce((a, b) => a + b, 0), charges };
};

const buildMovement = (DB) => {
  const news = [];
  const chns = [];
  let mx = 1;
  for (let i = 0; i < 13; i += 1) {
    let n = 0;
    let c = 0;
    orderOf(DB).forEach((t) => { n += S(DB, t)[i][0]; c += S(DB, t)[i][1]; });
    news.push(n); chns.push(c); mx = Math.max(mx, n, c);
  }
  const H = 58;
  let html = '';
  for (let i = 0; i < 13; i += 1) {
    let hu = Math.round((news[i] / mx) * H); if (news[i] > 0 && hu < 2) hu = 2;
    let hd = Math.round((chns[i] / mx) * H); if (chns[i] > 0 && hd < 2) hd = 2;
    const lbl = DB.months[i].l + ((i === 0 || i === 12 || DB.months[i].l === 'Jan') ? `<small>${DB.months[i].y}</small>` : '');
    const tip = i === 0 ? 'baseline month' : `${DB.months[i].l} ${DB.months[i].y}: +${news[i]} new · −${chns[i]} churned · net ${news[i] - chns[i] >= 0 ? '+' : ''}${news[i] - chns[i]}`;
    html += `<div class="mvcol"><div class="mvup" title="${esc(tip)}"><span class="mvl g">${i === 0 ? '' : `+${news[i]}`}</span>${i === 0 ? '' : `<div class="mvbar" style="height:${hu}px;background:var(--green)"></div>`}</div>`
      + `<div class="mvaxis"></div>`
      + `<div class="mvdn" title="${esc(tip)}">${i === 0 ? '' : `<div class="mvbar" style="height:${hd}px;background:var(--red)"></div>`}<span class="mvl r">${i === 0 ? '' : `−${chns[i]}`}</span></div>`
      + `<div class="mlab">${lbl}</div></div>`;
  }
  return html;
};

const buildWeekly = (DB) => {
  const W = DB.weekly;
  const L = W.labels.length;
  const vals = [W.goldM, W.lux, W.goldW];
  const meta = [['Gold · Men', 'var(--blue)', -8], ['Luxury · All', 'var(--p-lux)', 15], ['Gold · Women', '#b03a63', -8]];
  const all = [].concat(W.goldM, W.lux, W.goldW);
  const mn = Math.min.apply(null, all);
  const mx = Math.max.apply(null, all);
  const y0 = Math.max(0, Math.floor((mn - 45) / 50) * 50);
  const y1 = Math.ceil((mx + 45) / 50) * 50;
  const Wd = 980; const Ht = 252; const pl = 46; const pr = 26; const pt = 14; const pb = 30;
  const X = (i) => pl + (i * (Wd - pl - pr)) / (L - 1);
  const Y = (v) => pt + (Ht - pt - pb) * (1 - (v - y0) / (y1 - y0 || 1));
  let g = '';
  for (let t = 0; t <= 4; t += 1) {
    const gv = y0 + ((y1 - y0) * t) / 4;
    const yy = Y(gv);
    g += `<line x1="${pl}" y1="${yy}" x2="${Wd - pr}" y2="${yy}" stroke="var(--line)" stroke-width="1"/>`
      + `<text x="${pl - 8}" y="${yy + 3}" text-anchor="end" font-size="9" fill="var(--txt-3)">${Math.round(gv)}</text>`;
  }
  meta.forEach((m0, si) => {
    const arr = vals[si];
    const pts = arr.map((v, i) => `${X(i)},${Y(v)}`).join(' ');
    g += `<polyline points="${pts}" fill="none" stroke="${m0[1]}" stroke-width="2.2" stroke-linejoin="round"/>`;
    arr.forEach((v, i) => {
      const lastP = i === L - 1;
      g += `<circle cx="${X(i)}" cy="${Y(v)}" r="${lastP ? 4 : 2.8}" fill="${m0[1]}"${lastP ? ' stroke="var(--gold)" stroke-width="2.5"' : ''}><title>${esc(W.labels[i])}: ${esc(m0[0])} ${v}</title></circle>`
        + `<text x="${X(i)}" y="${Y(v) + m0[2]}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${m0[1]}">${v}</text>`;
    });
  });
  W.labels.forEach((lb, i) => {
    g += `<text x="${X(i)}" y="${Ht - 8}" text-anchor="middle" font-size="9" fill="var(--txt-3)"${i === L - 1 ? ' font-weight="700"' : ''}>${esc(lb)}</text>`;
  });
  const svg = `<svg viewBox="0 0 ${Wd} ${Ht}" style="width:100%;min-width:840px;display:block" role="img" aria-label="Weekly paying members by segment">${g}</svg>`;
  const legend = meta.map((m0, si) => {
    const a = vals[si];
    const d = a[L - 1] - a[L - 2];
    return `<span><i class="sw" style="background:${m0[1]}"></i>${m0[0]} <b>${a[L - 1].toLocaleString()}</b><span class="wow ${d >= 0 ? 'up' : 'dn'}">${d >= 0 ? '+' : ''}${d} WoW</span></span>`;
  }).join('');
  return { svg, legend };
};

// ---- small filter-pill group -------------------------------------------------
const Pills = ({ options, value, onChange, sm }) => (
  <div className={`periods${sm ? ' sm' : ''}`}>
    {options.map(([v, label]) => (
      <button key={v} type="button" className={v === value ? 'on' : ''} onClick={() => onChange(v)}>{label}</button>
    ))}
  </div>
);

const STATUS_LABEL = { act: ['ACTIVE', 'act'], exp: ['EXPIRED', 'exp'], cxl: ['CANCELLED', 'cxl'] };

/**
 * ACCOUNT status — the seven canonical statuses the rest of the admin uses
 * (deleted > deactivated > suspended > approved > hidden > inactive > ghost, first match
 * wins). The server computes it from helpers/statsFilters.js, and names/meanings come from
 * utils/memberStatus.js, so this column agrees with the Dashboard / Signup Report / User
 * Management by construction. "Active" is not an account status (it is Approved + Hidden).
 *
 * This is a DIFFERENT axis from the neighbouring `Status` column, which is the
 * SUBSCRIPTION's state — the two legitimately disagree (a deleted account can still hold
 * an Active subscription; that's exactly why the report still counts its revenue). Each
 * chip carries a tooltip spelling out which axis it is. Approved has its own `appr` class
 * rather than reusing `act`, so it never borrows the subscription chip's naming.
 */
const acctChip = (key, cls, extra = '') => [statusLabel(key).toUpperCase(), cls, `Account: ${STATUS_DESCRIPTION[key]}${extra}`];
const ACCT_LABEL = {
  approved: acctChip('approved', 'appr'),
  hidden: acctChip('hidden', 'hid'),
  inactive: acctChip('inactive', 'inact'),
  ghost: acctChip('ghost', 'ghost'),
  deactivated: acctChip('deactivated', 'deact'),
  suspended: acctChip('suspended', 'susp'),
  deleted: acctChip('deleted', 'del', ' — the subscription and its payments are still counted'),
};

// Payment gateway, read off the subscription (the transaction schema carries no gateway
// field at all). Keys match the subscription model's enum exactly.
const GW_LABEL = {
  Razorpay: 'RAZORPAY', Paypal: 'PAYPAL', GooglePlay: 'GOOGLE PLAY',
  Apple: 'APPLE', Stripe: 'STRIPE',
};

// Client-side filter for placeholder mode (live mode filters server-side).
const filterSample = (sf, page, limit) => {
  const q = sf.q.trim().toLowerCase();
  const rows = SAMPLE_DB.subs.filter((x) => {
    if (sf.plan !== 'all' && x.plan !== sf.plan) return false;
    if (sf.status !== 'all' && x.status !== sf.status) return false;
    if (sf.gender !== 'all' && x.g !== sf.gender) return false;
    if (sf.from && x.si < sf.from) return false;
    if (sf.to && x.si > sf.to) return false;
    if (q && `${x.name} ${x.hd} ${x.id}`.toLowerCase().indexOf(q) === -1) return false;
    return true;
  });
  const pages = Math.max(Math.ceil(rows.length / limit), 1);
  return { subs: rows.slice((page - 1) * limit, page * limit), subsTotal: rows.length, page, pages };
};

const SubscriptionReport = () => {
  // Member Engagement drawer — clicking a subscriber's "View" opens the same
  // slide-in engagement panel used on the Member Engagement tab, scoped to that
  // one member (fetched on demand via getMemberEngagement?userId=).
  const [eng, setEng] = useState({ open: false, loading: false, member: null, now: null, fallback: null });

  // `row` is the subscriber list row — its name/photo/deleted flag are used as a
  // guaranteed header fallback so the drawer always shows who it is, even if the
  // engagement fetch returns no data (e.g. a deleted member with no chat history).
  const openEngagement = useCallback(async (row) => {
    const fallback = { id: row.id, name: row.name, profilePicture: row.profilePicture, isDeletedUser: row.isDeletedUser };
    setEng({ open: true, loading: true, member: null, now: null, fallback });
    try {
      const data = await getMemberEngagement({ userId: row.id });
      const member = (data && data.members && data.members[0]) || null;
      setEng({ open: true, loading: false, member, now: data ? data.now : null, fallback });
    } catch (err) {
      setEng({ open: true, loading: false, member: null, now: null, fallback });
    }
  }, []);
  const closeEngagement = useCallback(() => setEng({ open: false, loading: false, member: null, now: null, fallback: null }), []);

  const [report, setReport] = useState(null); // aggregate blocks (set once)
  const [subsData, setSubsData] = useState(null); // { subs, subsTotal, page, pages }
  const [flag, setFlag] = useState('loading'); // loading | live | placeholder

  const [metric, setMetric] = useState('mrr');
  const [expanded, setExpanded] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [sf, setSf] = useState(EMPTY_SF);
  const [debouncedQ, setDebouncedQ] = useState('');

  const flagRef = useRef('loading');
  flagRef.current = flag;
  const hasAggregates = useRef(false); // aggregates loaded once; guards fallback

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(sf.q), 300);
    return () => clearTimeout(t);
  }, [sf.q]);

  // Any filter change resets to page 1 (reset during render, not in an effect).
  const sfKey = JSON.stringify({ ...sf, q: debouncedQ });
  const [pageKey, setPageKey] = useState(sfKey);
  if (pageKey !== sfKey) { setPageKey(sfKey); setPage(1); }

  const load = useCallback(async () => {
    const params = { ...sf, q: debouncedQ, page, limit: PAGE_SIZE };
    try {
      const data = await getSubscriptionReport(params);
      if (!data) throw new Error('empty');
      if (!hasAggregates.current) { setReport(data); hasAggregates.current = true; } // stable — set once
      setSubsData({ subs: data.subs || [], subsTotal: data.subsTotal || 0, page: data.page || 1, pages: data.pages || 1 });
      setFlag('live');
    } catch (err) {
      // Fall back to the mockup sample; still honour the table filters client-side.
      // Only seed sample aggregates if none have loaded — a transient sub-fetch
      // failure must not clobber already-loaded live aggregates.
      if (!hasAggregates.current) {
        setReport(SAMPLE_DB);
        hasAggregates.current = true;
        setSubsData(filterSample({ ...sf, q: debouncedQ }, page, PAGE_SIZE));
        setFlag('placeholder');
      }
    }
    setExpanded(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sfKey, page]);

  useEffect(() => { load(); }, [load]);

  const DB = report;

  // Upper bound for the Subscribed date filter (YYYY-MM-DD, IST). Read per render
  // rather than frozen in state so a tab left open overnight rolls over to the new
  // day. Distinct from `today` further down, which is a long-form display string.
  const maxDate = istToday();

  // ---- KPI + strip computations (from the aggregate cube) --------------------
  const view = useMemo(() => {
    if (!DB) return null;
    let payNow = 0; let payJun = 0; let mrrNow = 0; let mrrJun = 0; let femNow = 0;
    let newMTD = 0; let chnMTD = 0; let newJun = 0; let chnJun = 0; let activeMay = 0;
    orderOf(DB).forEach((t) => {
      const s = S(DB, t);
      payNow += s[12][2]; payJun += s[11][2];
      mrrNow += R(DB, t, 12, C_ACT); mrrJun += R(DB, t, 11, C_ACT);
      femNow += Math.round(s[12][2] * DB.fshare[t]);
      newMTD += s[12][0]; chnMTD += s[12][1]; newJun += s[11][0]; chnJun += s[11][1]; activeMay += s[10][2];
    });
    const dm = mrrJun ? (100 * (mrrNow - mrrJun)) / mrrJun : 0;
    return { payNow, payJun, mrrNow, mrrJun, femNow, newMTD, chnMTD, newJun, chnJun, activeMay, dm };
  }, [DB]);

  const summary = useMemo(() => {
    if (!DB) return null;
    let Sn = 0; let Sc = 0; let junN = 0; let junC = 0; let julN = 0; let julC = 0; let act = 0;
    orderOf(DB).forEach((t) => {
      const r = S(DB, t);
      for (let i = 1; i < 13; i += 1) { Sn += r[i][0]; Sc += r[i][1]; }
      junN += r[11][0]; junC += r[11][1]; julN += r[12][0]; julC += r[12][1]; act += r[12][2];
    });
    return { ever: DB.life.preSub + Sn, gone: DB.life.preCxl + Sc, junN, junC, julN, julC, act, rc: DB.recent };
  }, [DB]);

  const revSummary = useMemo(() => {
    if (!DB) return null;
    // Sum over EVERY plan the report has rows for, like every other block does.
    // This used to hardcode 'lux' + 'gold' and so silently dropped Gold · Women
    // (`goldF`): the strip's "MRR · Today" read ₹6,000 below the headline KPI —
    // with its own contradicting delta — and new/churned revenue was short by
    // that plan too. A new plan variant now lands here with no code change.
    const pm = (i, j) => orderOf(DB).reduce((a, t) => a + R(DB, t, i, j), 0);
    const julN = pm(12, C_NEW);
    const julC = pm(12, C_CHN);
    const junN = pm(11, C_NEW);
    const junC = pm(11, C_CHN);
    const mrrNow = pm(12, C_ACT);
    const mrrJun = pm(11, C_ACT);
    const dm = mrrJun ? (100 * (mrrNow - mrrJun)) / mrrJun : 0;
    return {
      julN, julC, junN, junC, mrrNow, dm,
      R: DB.recentR || { n7: 0, c7: 0, nY: 0, cY: 0, nT: 0, cT: 0, s7: 0, sY: 0, sT: 0 },
    };
  }, [DB]);

  const planRows = useMemo(() => {
    if (!DB) return null;
    let totM = 0; let totP = 0; let totF = 0; let totN = 0; let totC = 0; let totPrev = 0; let maxMRR = 1;
    const mrrs = {};
    orderOf(DB).forEach((t) => { const m = R(DB, t, 12, C_ACT); mrrs[t] = m; maxMRR = Math.max(maxMRR, m); totM += m; });
    const rows = orderOf(DB).map((t) => {
      const s = S(DB, t); const a = s[12][2]; const f = Math.round(a * DB.fshare[t]); const n = s[12][0]; const c = s[12][1];
      // Churn rate over the SAME period as the New/Churned columns beside it and the
      // panel's own "as of today". It used to read last month's churn over the month
      // BEFORE last — a different period from its two neighbours, so a row showed
      // "5 churned" next to a rate computed from a month those 5 are not in.
      //
      // Denominator is everyone AT RISK during the month — active at the end of last
      // month PLUS those who joined during it. Dividing by the opening balance alone
      // is the textbook form but breaks on this data's volumes: members routinely
      // join and leave inside the same month, so Luxury read 4 churned over 1 opening
      // member = 400%. A share of members that exceeds every member is a defect, not
      // an insight. This form is bounded by 100% and answers the question the column
      // is actually asked: of everyone we had this month, how many left?
      // Month-to-date, so it climbs through the month.
      const base = s[11][2] + n;
      const cr = base ? (100 * c) / base : 0;
      totP += a; totF += f; totN += n; totC += c; totPrev += base;
      return { t, a, f, n, c, cr, mrr: mrrs[t], share: totM ? Math.round((100 * mrrs[t]) / totM) : 0, bar: Math.round((100 * mrrs[t]) / maxMRR) };
    });
    return { rows, totM, totP, totF, totN, totC, totCr: totPrev ? (100 * totC) / totPrev : 0 };
  }, [DB]);

  const mainChart = useMemo(() => (DB ? buildMainChart(DB, metric === 'mrr') : null), [DB, metric]);
  // Absent on the mockup sample fallback, which has no ledger behind it — the panel
  // is skipped rather than faked, so a placeholder can't be read as real cash.
  const cashChart = useMemo(() => ((DB && DB.collected && DB.collected.total) ? buildCashChart(DB) : null), [DB]);
  const weekly = useMemo(() => (DB ? buildWeekly(DB) : null), [DB]);
  const movement = useMemo(() => (DB ? buildMovement(DB) : null), [DB]);

  const toggleRow = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const today = useMemo(() => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), []);
  const seg = (k, inner, sub) => (
    <div className="sseg"><div className="sk">{k}</div>{inner}{sub ? <div className="ssub">{sub}</div> : null}</div>
  );
  // `s` = plan changes in the same window (upgrades + downgrades). A switch is a THIRD
  // movement — neither a signup nor a churn — because the member never stopped paying;
  // see the `chg` bucket in SubscriptionReportController. Without it the day a member
  // moved Luxury → Gold read "0 new / 0 churned" while MRR quietly fell. Rendered only
  // when there is one, so the strip is unchanged on the days nobody switches.
  const pairNet = (n, c, dollars, s) => (
    <>
      <div className="srow pn">{`+${dollars ? money(n) : n.toLocaleString()} new`}</div>
      <div className="srow px">{`−${dollars ? money(c) : c.toLocaleString()} ${dollars ? 'churned' : 'cancelled'}`}</div>
      {s ? (
        <div className="srow ps">
          {dollars
            ? `${s >= 0 ? '+' : '−'}${money(Math.abs(s))} plan changes`
            : `${s.toLocaleString()} plan change${Math.abs(s) === 1 ? '' : 's'}`}
        </div>
      ) : null}
    </>
  );
  const netLabel = (n, c, dollars, s) => {
    // A plan change moves MONEY but not MEMBERSHIP, so its delta belongs in the ₹ net and
    // must stay out of the member net. That is what keeps each "net" agreeing with the KPI
    // above it — MRR for money, Paying Members for people.
    const d = dollars ? (n - c + (s || 0)) : (n - c);
    return dollars
      ? `net ${d >= 0 ? '+' : '−'}${money(Math.abs(d))}`
      : `net ${d >= 0 ? '+' : ''}${d}`;
  };

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/financials.svg"
          text="Subscription Report"
          description="Paying members, revenue and movement across the Luxury and Gold plans."
        />
        {flag === 'placeholder' && (
          <Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" />
        )}
      </Stack>

      <div className="favor-sr">
        {!DB ? (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        ) : (
          <>
            <div className="ctx">{today} &middot; current-month figures are month-to-date</div>

            {/* KPIs */}
            <div className="stats">
              <div className="stat"><span className="bar" /><div className="k">Monthly Recurring Revenue</div>
                <div className="v">{money(view.mrrNow)}<span className={`delta ${view.dm >= 0 ? 'up' : 'dn'}`}>{`${view.dm >= 0 ? '+' : ''}${view.dm.toFixed(1)}%`}</span></div>
                <div className="d">{`vs ${money(view.mrrJun)} end of last month`}</div>
              </div>
              <div className="stat sg"><span className="bar" /><div className="k">Paying Members</div>
                <div className="v">{view.payNow.toLocaleString()}</div>
                <div className="d">{`Men ${(view.payNow - view.femNow).toLocaleString()} · Women ${view.femNow.toLocaleString()} (${view.payNow ? Math.round((100 * view.femNow) / view.payNow) : 0}%)`}</div>
              </div>
              <div className="stat sn"><span className="bar" /><div className="k">New &middot; This Month</div>
                <div className="v">{view.newMTD.toLocaleString()}</div>
                <div className="d">{`Last month total: ${view.newJun.toLocaleString()}`}</div>
              </div>
              <div className="stat sc"><span className="bar" /><div className="k">Churned &middot; This Month</div>
                <div className="v">{view.chnMTD.toLocaleString()}</div>
                {/* Same at-risk denominator as the Plan Economics churn column: last
                    month's opening balance PLUS everyone who joined during it. Over the
                    opening balance alone this read "200.0% of members", which is not a
                    share of anything. */}
                <div className="d">{`Last month: ${view.chnJun.toLocaleString()} · ${(view.activeMay + view.newJun) ? ((100 * view.chnJun) / (view.activeMay + view.newJun)).toFixed(1) : '0.0'}% of members`}</div>
              </div>
            </div>

            {/* At a Glance */}
            <div className="panel">
              <div className="phead"><h2>At a Glance</h2><span className="pmeta">SUBSCRIPTIONS &middot; AS OF TODAY</span></div>
              <div className="sumstrip">
                {seg('Subscribers · Till Date', <div className="sbig">{summary.ever.toLocaleString()}</div>, `since launch, ${DB.life.launch}`)}
                {seg('Cancelled · Till Date', <div className="sbig">{summary.gone.toLocaleString()}</div>, `= ${summary.act.toLocaleString()} still active`)}
                {seg('This Month · MTD', pairNet(summary.julN, summary.julC, false), netLabel(summary.julN, summary.julC, false))}
                {seg('Last Month', pairNet(summary.junN, summary.junC, false), netLabel(summary.junN, summary.junC, false))}
                {seg('Last 7 Days', pairNet(summary.rc.n7, summary.rc.c7, false, summary.rc.s7), netLabel(summary.rc.n7, summary.rc.c7, false))}
                {seg('Yesterday', pairNet(summary.rc.nY, summary.rc.cY, false, summary.rc.sY), netLabel(summary.rc.nY, summary.rc.cY, false))}
                {seg('Today', pairNet(summary.rc.nT, summary.rc.cT, false, summary.rc.sT), netLabel(summary.rc.nT, summary.rc.cT, false))}
              </div>
            </div>

            {/* Revenue at a Glance */}
            <div className="panel">
              <div className="phead"><h2>Revenue at a Glance</h2><span className="pmeta">MRR MOVEMENT &middot; AS OF TODAY</span></div>
              <div className="sumstrip">
                {seg('Revenue · Till Date', <div className="sbig">{moneyM(DB.revlife || 0)}</div>, `collected since launch, ${DB.life.launch}`)}
                {seg('MRR · Today', <div className="sbig">{money(revSummary.mrrNow)}</div>, `${revSummary.dm >= 0 ? '+' : ''}${revSummary.dm.toFixed(1)}% vs end of last month`)}
                {seg('This Month · MTD', pairNet(revSummary.julN, revSummary.julC, true), netLabel(revSummary.julN, revSummary.julC, true))}
                {seg('Last Month', pairNet(revSummary.junN, revSummary.junC, true), netLabel(revSummary.junN, revSummary.junC, true))}
                {seg('Last 7 Days', pairNet(revSummary.R.n7, revSummary.R.c7, true, revSummary.R.s7), netLabel(revSummary.R.n7, revSummary.R.c7, true, revSummary.R.s7))}
                {seg('Yesterday', pairNet(revSummary.R.nY, revSummary.R.cY, true, revSummary.R.sY), netLabel(revSummary.R.nY, revSummary.R.cY, true, revSummary.R.sY))}
                {seg('Today', pairNet(revSummary.R.nT, revSummary.R.cT, true, revSummary.R.sT), netLabel(revSummary.R.nT, revSummary.R.cT, true, revSummary.R.sT))}
              </div>
            </div>

            {/* Revenue / Members by Month */}
            <div className="panel">
              <div className="phead">
                <h2>{metric === 'mrr' ? 'Revenue by Month' : 'Paying Members by Month'}</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Pills options={[['mrr', 'MRR'], ['mem', 'Members']]} value={metric} onChange={setMetric} />
                  <span className="pmeta">{`${DB.months[0].l.toUpperCase()} ${DB.months[0].y} – ${DB.months[12].l.toUpperCase()} ${DB.months[12].y}`}</span>
                </div>
              </div>
              <div className="psub">Stacked by plan. The current month is month-to-date and outlined in gold.</div>
              <div className="legend" dangerouslySetInnerHTML={{ __html: mainChart.legend }} />
              <div className="chartwrap"><div className="chart" dangerouslySetInnerHTML={{ __html: mainChart.chart }} /></div>
            </div>

            {/* Revenue Collected by Month — cash in, all statuses */}
            {cashChart && (
              <div className="panel">
                <div className="phead">
                  <h2>Revenue Collected by Month</h2>
                  <span className="pmeta">{`CASH RECEIVED · ${DB.months[0].l.toUpperCase()} ${DB.months[0].y} – ${DB.months[12].l.toUpperCase()} ${DB.months[12].y}`}</span>
                </div>
                <div className="psub">
                  {`Every payment actually received in that month — ${money(cashChart.total)} across ${cashChart.charges.toLocaleString()} charges — counted whether the member is still subscribed, cancelled, expired or deleted. Unlike the chart above (a monthly run rate), a quarterly or annual payment is counted in full in the month it was taken, so a renewal-heavy month can stand well above MRR. Stacked by plan; the current month is month-to-date and outlined in gold.`}
                </div>
                <div className="legend" dangerouslySetInnerHTML={{ __html: cashChart.legend }} />
                <div className="chartwrap"><div className="chart" dangerouslySetInnerHTML={{ __html: cashChart.chart }} /></div>
              </div>
            )}

            {/* Weekly */}
            <div className="panel">
              <div className="phead"><h2>Paying Members &mdash; Week on Week</h2><span className="pmeta">LAST 13 WEEKS &middot; WEEKLY SNAPSHOTS</span></div>
              <div className="psub">Subscriber counts at the end of each week for Gold men, Gold women and the Luxury plan. The gold-ringed dots are today.</div>
              <div className="legend" dangerouslySetInnerHTML={{ __html: weekly.legend }} />
              <div className="chartwrap"><div style={{ marginTop: 12 }} dangerouslySetInnerHTML={{ __html: weekly.svg }} /></div>
            </div>

            {/* Movement */}
            <div className="panel">
              <div className="phead"><h2>Subscriber Movement</h2><span className="pmeta">NEW VS CHURNED &middot; BOTH PLANS</span></div>
              <div className="psub">Green above the line = new paying members that month; red below = cancellations and failed renewals. The current month is month-to-date.</div>
              <div className="chartwrap"><div className="mvchart" dangerouslySetInnerHTML={{ __html: movement }} /></div>
            </div>

            {/* Plan Economics */}
            <div className="panel">
              <div className="phead"><h2>Plan Economics</h2><span className="pmeta">AS OF TODAY</span></div>
              <div className="tblwrap stickyhead">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Plan</th><th className="num">Members</th><th>Split</th>
                      <th className="num" title="Sum of what each active member is actually billed each month — not Members × the list price beside the plan name, because members who joined before a price change stay on their original price.">MRR</th>
                      <th className="num">Share</th><th className="num">New (mo)</th><th className="num">Churned (mo)</th>
                      <th className="num" title="Cancellations this month so far, as a share of the members active at the end of last month. Month-to-date, like the two columns before it.">Churn / mo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planRows.rows.map((r) => (
                      <tr key={r.t}>
                        {/* Current list price. MRR beside it is the sum of what each
                            member actually pays, so the two do not multiply out for a
                            plan whose members sit on more than one price. */}
                        <td><span className="tdot" style={{ background: PCOL[r.t] }} /><b>{DB.planNames[r.t]}</b><span className="tprice" title={`Current list price for new members. Anyone who joined before a price change keeps the price they signed up at, so MRR is not always Members × ${money(DB.prices[r.t])}.`}>{`${money(DB.prices[r.t])}/mo`}</span></td>
                        <td className="num">{r.a.toLocaleString()}</td>
                        <td><span className="mf">{`M ${(r.a - r.f).toLocaleString()} · W ${r.f.toLocaleString()}`}</span></td>
                        <td className="num"><b>{money(r.mrr)}</b><div className="ibar"><i style={{ width: `${r.bar}%`, background: PCOL[r.t] }} /></div></td>
                        <td className="num">{`${r.share}%`}</td>
                        <td className="num">{r.n}</td>
                        <td className="num">{r.c}</td>
                        <td className="num">{`${r.cr.toFixed(1)}%`}</td>
                      </tr>
                    ))}
                    <tr className="tot">
                      {/* "Both plans" was written when there were two rows; the report
                          has carried a row per PLAN (Luxury / Gold · Men / Gold · Women)
                          since pricing went per tier x gender. */}
                      <td>All plans</td>
                      <td className="num">{planRows.totP.toLocaleString()}</td>
                      <td><span className="mf">{`M ${(planRows.totP - planRows.totF).toLocaleString()} · W ${planRows.totF.toLocaleString()}`}</span></td>
                      <td className="num">{money(planRows.totM)}</td>
                      <td className="num">100%</td>
                      <td className="num">{planRows.totN}</td>
                      <td className="num">{planRows.totC}</td>
                      {/* Blended rate = all cancellations over all members who could
                          cancel — NOT the mean of the per-plan rates, which would weight
                          a 3-member plan like a 300-member one. */}
                      <td className="num">{`${planRows.totCr.toFixed(1)}%`}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Subscribers */}
            <div className="panel">
              <div className="phead"><h2>Subscribers</h2><span className="pmeta">{`${(subsData?.subsTotal || 0).toLocaleString()} MEMBERS`}</span></div>
              <div className="psub">Full lifecycle per member: profile creation to completion, time to first purchase, every renewal with the plan charged, and cancellation or expiry. Click a renewal count to see the history; the Messaging link opens the member&rsquo;s history page.</div>
              <div className="sfilters">
                <input className="srch" type="text" placeholder="Search name, @username or ID" value={sf.q} onChange={(e) => setSf((s) => ({ ...s, q: e.target.value }))} />
                {/* Driven by `planOrder`, like every other plan-aware block here.
                    It was hardcoded to [All, Luxury, Gold] from before pricing went
                    per tier x gender, so "Gold" matched the key `gold` exactly and
                    silently excluded all 6 Gold · Women members (`goldF`) — who had
                    no option of their own either, so they were unreachable. */}
                <div className="fgrp"><span className="fl">Plan</span><Pills sm options={[['all', 'All'], ...orderOf(DB).map((k) => [k, DB.planNames[k] || k])]} value={sf.plan} onChange={(v) => setSf((s) => ({ ...s, plan: v }))} /></div>
                <div className="fgrp"><span className="fl">Status</span><Pills sm options={[['all', 'All'], ['act', 'Active'], ['cxl', 'Cancelled'], ['exp', 'Expired']]} value={sf.status} onChange={(v) => setSf((s) => ({ ...s, status: v }))} /></div>
                <div className="fgrp"><span className="fl">Gender</span><Pills sm options={[['all', 'All'], ['F', 'Women'], ['M', 'Men']]} value={sf.gender} onChange={(v) => setSf((s) => ({ ...s, gender: v }))} /></div>
                {/* Nobody subscribed tomorrow, so a future date can only ever return
                    an empty table that looks like a data problem. Both inputs are
                    capped at today (IST — the day the server filters on). */}
                <div className="fgrp"><span className="fl">Subscribed</span>
                  <input
                    type="date"
                    className="dinp"
                    max={maxDate}
                    value={sf.from}
                    onChange={(e) => { const v = notFuture(e.target.value, maxDate); setSf((s) => ({ ...s, from: v })); }}
                  />
                  <span className="fl" style={{ letterSpacing: 0 }}>to</span>
                  <input
                    type="date"
                    className="dinp"
                    max={maxDate}
                    value={sf.to}
                    onChange={(e) => { const v = notFuture(e.target.value, maxDate); setSf((s) => ({ ...s, to: v })); }}
                  />
                </div>
                {/* One control that puts every filter back to "all" — the date
                    inputs in particular are fiddly to empty by hand. Hidden until
                    something is actually set so the bar stays quiet by default. */}
                {isFiltered(sf) && (
                  <button type="button" className="clrf" onClick={() => setSf(EMPTY_SF)}>Clear filters</button>
                )}
              </div>
              <div className="tblwrap stickyv">
                <table className="tbl stbl">
                  <thead>
                    <tr>
                      <th>S.No.</th><th>Member</th><th>Gender</th><th>Account</th><th>Plan</th><th>Gateway</th><th>Device</th>
                      {/* WEB-64. Money actually received, not the plan's list price:
                          every Completed charge this member has made, each currency
                          converted to INR (PayPal bills in USD). It stops accruing when
                          they cancel, so it can be lower than plan price × months. */}
                      <th className="num" title="Total actually paid — every completed charge, converted to INR">Paid (INR)</th>
                      <th>Status</th>
                      <th>Profile created</th><th>Profile completed</th><th>Subscribed</th><th className="num">Days to subscribe</th>
                      <th>Renewals</th><th>History</th><th>Cancelled on</th><th className="num">Days to cancel</th><th>Messaging</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!subsData || !subsData.subs.length ? (
                      <tr><td colSpan={18} style={{ padding: 30, textAlign: 'center', color: 'var(--txt-3)' }}>No members match the current filters.</td></tr>
                    ) : subsData.subs.map((x, ri) => {
                      const st = STATUS_LABEL[x.status] || ['—', 'act'];
                      // No entry = the sample-data fallback (which carries no account
                      // state). Render an honest dash rather than guessing at a bucket.
                      const ac = ACCT_LABEL[x.acct];
                      const mixP = [];
                      if (x.renG) mixP.push(`${x.renG} Gold`);
                      if (x.renL) mixP.push(`${x.renL} Luxury`);
                      const rowNo = (page - 1) * PAGE_SIZE + ri + 1;
                      const isOpen = expanded.has(x.id);
                      return (
                        <React.Fragment key={x.id}>
                          <tr>
                            <td className="snum">{rowNo}</td>
                            <td>
                              <div className="mcell">
                                <div className="avat" style={x.profilePicture ? { backgroundImage: `url(${x.profilePicture})` } : { background: x.grad }} />
                                <div>
                                  {/* The old inline "deleted" chip lived here; the Account
                                      column now states it (and catches live soft-deleted
                                      docs the chip missed), so showing both just said the
                                      same word twice in one row. */}
                                  <div className="mn">{x.name}<span className="idch">{`#${String(x.id).slice(-4).toUpperCase()}`}</span></div>
                                  <div className="mh">{x.hd}</div>
                                </div>
                              </div>
                            </td>
                            <td>{x.g === 'F' ? <span className="gch f">FEMALE</span> : x.g === 'M' ? <span className="gch m">MALE</span> : <span className="gch o">OTHER</span>}</td>
                            <td>{ac ? <span className={`ast ${ac[1]}`} title={ac[2]}>{ac[0]}</span> : '—'}</td>
                            <td><span className="tdot" style={{ background: PCOL[x.plan] }} />{DB.planNames[x.plan]}</td>
                            {/* Gateway comes off the SUBSCRIPTION, not the transaction —
                                the transaction schema has no paymentGateway field, so the
                                ledger carries no gateway attribution at all. */}
                            <td>{x.gw ? <span className={`gwch ${String(x.gw).toLowerCase()}`}>{GW_LABEL[x.gw] || x.gw}</span> : '—'}</td>
                            <td>{x.device || '—'}</td>
                            {/* "—" when the field is absent (sample-data fallback); a
                                genuine subscriber always cleared a real payment, so a
                                real 0 here would mean the ledger lost the charge and
                                should not be dressed up as ₹0. */}
                            <td className="num">
                              {x.paid == null ? '—' : (
                                <>
                                  <div className="paidv">{money(x.paid)}</div>
                                  {x.paidCharges > 1 && <div className="mh">{`${x.paidCharges} charges`}</div>}
                                </>
                              )}
                            </td>
                            <td><span className={`sst ${st[1]}`}>{st[0]}</span></td>
                            <td>{x.created}</td><td>{x.completed}</td><td>{x.subscribed}</td>
                            <td className="num">{x.d2s != null ? <span className="dchip">{`${x.d2s} d`}</span> : '—'}</td>
                            <td className="rencell">
                              <div className="rrow"><b>{x.renN}</b></div>
                              {x.renN > 0 ? <div className="mh rmix">{mixP.join(' · ')}</div> : null}
                            </td>
                            <td>{x.renN > 0 ? <button type="button" className="xbt" onClick={() => toggleRow(x.id)}>{isOpen ? 'Hide ▴' : 'View ▾'}</button> : '—'}</td>
                            <td>{x.xdate || '—'}</td>
                            <td className="num">{x.d2c != null ? <span className="dchip">{`${x.d2c} d`}</span> : '—'}</td>
                            <td><button type="button" className="msglink" onClick={() => openEngagement(x)}>View &#8599;</button></td>
                          </tr>
                          {isOpen && (
                            <tr className="exprow">
                              <td colSpan={18}>
                                <div className="rh">
                                  <span className="rk">PLAN &amp; RENEWAL HISTORY</span>
                                  {x.renH.map((h, hi) => {
                                    if (h[2] === 'x') return <span key={hi} className="rc x"><b>CANCELLED</b> &middot; {h[0]}</span>;
                                    if (h[2] === 'e') return <span key={hi} className="rc e"><b>EXPIRED</b> &middot; {h[0]} (payment failed, not renewed)</span>;
                                    const pre = h[2] === 's' ? <b>STARTED &middot; </b> : h[2] === 'u' ? <b>&uarr; UPGRADED &middot; </b> : null;
                                    // Chip colour is TIER-level, so a gender variant (goldF) still reads as Gold.
                                    return <span key={hi} className={`rc ${String(h[1]).startsWith('gold') ? 'g' : 'l'}`}>{pre}{h[0]} &middot; {DB.planNames[h[1]] || ''}</span>;
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {subsData && subsData.pages > 1 && (
                <div className="pager">
                  <span>{`Page ${subsData.page} of ${subsData.pages}`}</span>
                  <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                  <button type="button" disabled={page >= subsData.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <MemberEngagementDrawer
        open={eng.open}
        loading={eng.loading}
        member={eng.member}
        now={eng.now}
        fallback={eng.fallback}
        onClose={closeEngagement}
      />
    </Box>
  );
};

export default SubscriptionReport;
